import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';

import { type CoverageView } from '@apps/web/features/coverage/model/dashboard_model';
import { EVENTS } from '@apps/web/lib/events';

// Product analytics for the Coverage dashboard page: view counts, which tab
// is used, and time spent per tab and per page visit. No caregiver names or
// ids ever appear in these events.
//
// Purely reactive to currentView rather than requiring every state-changing
// call site (setView, selectCoverageCell's implicit jump to Grid) to
// remember to report a tab change.
export function useCoverageAnalytics({
    organizationId,
    isReady,
    currentView,
}: {
    readonly organizationId: string | null;
    readonly isReady: boolean;
    readonly currentView: CoverageView;
}): void {
    const posthog = usePostHog();
    const organizationIdRef = useRef(organizationId);
    organizationIdRef.current = organizationId;
    const posthogRef = useRef(posthog);
    posthogRef.current = posthog;

    const pageEnteredAtRef = useRef(Date.now());
    const previousViewRef = useRef<CoverageView | null>(null);
    const viewEnteredAtRef = useRef(Date.now());
    const pageViewedForOrgRef = useRef<string | null>(null);

    const capture = useCallback(
        (eventName: string, properties: Record<string, unknown>) => {
            try {
                posthogRef.current?.capture(eventName, {
                    organization_id: organizationIdRef.current,
                    ...properties,
                });
            } catch {
                // Best-effort: analytics must never break the page it measures.
            }
        },
        [],
    );

    useEffect(() => {
        if (
            !isReady ||
            organizationId === null ||
            pageViewedForOrgRef.current === organizationId
        ) {
            return;
        }
        pageViewedForOrgRef.current = organizationId;
        previousViewRef.current = currentView;
        viewEnteredAtRef.current = Date.now();
        capture(EVENTS.COVERAGE_PAGE_VIEWED, { initial_view: currentView });
    }, [capture, currentView, isReady, organizationId]);

    useEffect(() => {
        if (
            pageViewedForOrgRef.current === null ||
            previousViewRef.current === null ||
            previousViewRef.current === currentView
        ) {
            return;
        }
        const now = Date.now();
        capture(EVENTS.COVERAGE_TAB_CHANGED, {
            from_view: previousViewRef.current,
            to_view: currentView,
            time_on_previous_view_ms: now - viewEnteredAtRef.current,
        });
        previousViewRef.current = currentView;
        viewEnteredAtRef.current = now;
    }, [capture, currentView]);

    useEffect(() => {
        const enteredAt = pageEnteredAtRef.current;
        return () => {
            if (pageViewedForOrgRef.current === null) {
                return;
            }
            capture(EVENTS.COVERAGE_PAGE_LEFT, {
                final_view: previousViewRef.current,
                total_time_on_page_ms: Date.now() - enteredAt,
            });
        };
    }, [capture]);
}
